import React from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { type PriorityLevel } from './priority';
import { type PriorityCounts } from '../../../hooks/useWorkQueue';

interface PrioritySectionDividerProps {
    level: PriorityLevel;
    priorityCounts?: PriorityCounts;
    colSpan: number;
    collapsed: boolean;
    onToggle: () => void;
}

interface LevelStyle {
    label: string;
    textClass: string;
    bgClass: string;
    hoverClass: string;
}

const LEVEL_STYLES: Record<PriorityLevel, LevelStyle> = {
    extreme: {
        label: 'EXTREME',
        textClass: 'text-red-700',
        bgClass: 'bg-red-100',
        hoverClass: 'hover:bg-red-200'
    },
    high: {
        label: 'HIGH',
        textClass: 'text-amber-700',
        bgClass: 'bg-amber-100',
        hoverClass: 'hover:bg-amber-200'
    },
    normal: {
        label: 'NORMAL',
        textClass: 'text-gray-600',
        bgClass: 'bg-gray-100',
        hoverClass: 'hover:bg-gray-200'
    }
};

export function PrioritySectionDivider({
    level,
    priorityCounts,
    colSpan,
    collapsed,
    onToggle
}: PrioritySectionDividerProps): React.ReactElement {
    const style = LEVEL_STYLES[level];
    const count = priorityCounts?.[level];
    const ChevronIcon = collapsed ? ChevronRightIcon : ChevronDownIcon;

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
        }
    }

    return (
        <tr
            role="button"
            aria-expanded={!collapsed}
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={handleKeyDown}
            className={`cursor-pointer transition-colors ${style.bgClass} ${style.hoverClass}`}
        >
            <td colSpan={colSpan} className="p-0">
                <div className="flex items-center gap-1.5 py-[3px] px-4">
                    <ChevronIcon
                        className={`w-3 h-3 flex-shrink-0 ${style.textClass}`}
                        aria-hidden="true"
                    />
                    <span
                        className={`text-[10px] font-bold tracking-[0.08em] uppercase whitespace-nowrap ${style.textClass}`}
                    >
                        {style.label}
                    </span>
                    {count != null && (
                        <span className={`text-[10px] font-semibold opacity-60 ${style.textClass}`}>
                            · {count}
                        </span>
                    )}
                </div>
            </td>
        </tr>
    );
}
